/**
 * Templates Page
 * Tabbed page combining platform templates (read-only, preview & download)
 * with project management and custom template CRUD.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  X,
  Eye,
  Download,
  Target,
  LayoutDashboard,
  GitBranch,
  ClipboardList,
  Flag,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Sparkles,
  Plus,
  Edit3,
  Trash2,
  Save,
  ChevronDown,
  FolderOpen,
  FolderKanban,
  AlertCircle,
} from 'lucide-react';
import { PageLayout } from '@/components';
import { AppPageHeader, Button, CardAction, UnauthenticatedPage } from '@/components/ui';
import { MarkdownPreview, WysiwygEditor } from '@/components/markdown';
import { toast } from 'sonner';
import { useAuthContext } from '@/contexts/AuthContext';
import { useTemplates } from '@/hooks/useTemplates';
import { useAssignments } from '@/hooks/useAssignments';
import { useProjectConfig } from '@/hooks/useProjectConfig';
import { TemplatePickerModal } from '@/components/TemplatePickerModal';
import { ProjectCard } from '@/pages/project-config/components/ProjectCard';
import { ProjectModal } from '@/pages/project-config/components/ProjectModal';
import { ShareProjectModal } from '@/pages/project-config/components/ShareProjectModal';
import { templatesApi } from '@/services/templatesApi';
import type { DefaultTemplateMetadata, TemplateMetadata, SaveTemplateRequest } from '@/services/templatesApi';
import type { ProjectConfig } from '@/lib/types';
import { errorMessages } from '@/lib/errors';
import { formatDate as formatDateLocale } from '@/lib/i18n-utils';

// Icon mapping for default templates (icon name from DB -> React component)
const ICON_MAP: Record<string, React.ElementType> = {
  Target: Target,
  Flag: Flag,
  LayoutDashboard: LayoutDashboard,
  GitBranch: GitBranch,
  FileText: FileText,
  ClipboardList: ClipboardList,
  Layers: GitBranch,
  BookOpen: FileText,
};

// Output format options for custom templates
const OUTPUT_FORMATS = [
  { value: 'meeting-notes', labelKey: 'templates.formatMeetingNotes' },
  { value: 'adr', labelKey: 'templates.formatAdr' },
  { value: 'solution-design', labelKey: 'templates.formatSolutionDesign' },
  { value: 'full', labelKey: 'templates.formatFull' },
  { value: 'custom', labelKey: 'templates.formatCustom' },
];

// Color options for custom template cards
const COLOR_OPTIONS = [
  { value: 'slate', labelKey: 'templates.colorGray', bg: 'bg-slate-100', bgDark: 'bg-slate-600', text: 'text-slate-600' },
  { value: 'blue', labelKey: 'templates.colorBlue', bg: 'bg-blue-100', bgDark: 'bg-blue-600', text: 'text-blue-600' },
  { value: 'emerald', labelKey: 'templates.colorGreen', bg: 'bg-emerald-100', bgDark: 'bg-emerald-600', text: 'text-emerald-600' },
  { value: 'purple', labelKey: 'templates.colorPurple', bg: 'bg-purple-100', bgDark: 'bg-purple-600', text: 'text-purple-600' },
  { value: 'amber', labelKey: 'templates.colorAmber', bg: 'bg-amber-100', bgDark: 'bg-amber-600', text: 'text-amber-600' },
  { value: 'rose', labelKey: 'templates.colorRose', bg: 'bg-rose-100', bgDark: 'bg-rose-600', text: 'text-rose-600' },
];

// Color classes helper for platform templates
const getColorClasses = (color: string) => {
  const colors: Record<string, { bg: string; hover: string; icon: string }> = {
    blue: { bg: 'bg-blue-100', hover: 'group-hover:bg-blue-200', icon: 'text-blue-600' },
    emerald: { bg: 'bg-emerald-100', hover: 'group-hover:bg-emerald-200', icon: 'text-emerald-600' },
    purple: { bg: 'bg-purple-100', hover: 'group-hover:bg-purple-200', icon: 'text-purple-600' },
    amber: { bg: 'bg-amber-100', hover: 'group-hover:bg-amber-200', icon: 'text-amber-600' },
    rose: { bg: 'bg-rose-100', hover: 'group-hover:bg-rose-200', icon: 'text-rose-600' },
    indigo: { bg: 'bg-indigo-100', hover: 'group-hover:bg-indigo-200', icon: 'text-indigo-600' },
    slate: { bg: 'bg-slate-100', hover: 'group-hover:bg-slate-200', icon: 'text-slate-600' },
  };
  return colors[color] || colors.slate;
};

// Color classes helper for custom templates
const getCustomColorClasses = (color?: string) => {
  const colorOption = COLOR_OPTIONS.find(c => c.value === color) || COLOR_OPTIONS[0];
  return { bg: colorOption.bg, bgDark: colorOption.bgDark, text: colorOption.text };
};

// Editor state for custom template create/edit modal
interface TemplateEditorState {
  isOpen: boolean;
  mode: 'create' | 'edit';
  templateId?: string;
  name: string;
  description: string;
  content: string;
  outputFormat: string;
  color: string;
  reviewMode: 'preview' | 'edit';
  hasUnsavedChanges: boolean;
  originalContent: string;
}

const initialEditorState: TemplateEditorState = {
  isOpen: false,
  mode: 'create',
  name: '',
  description: '',
  content: '# Template Title\n\n## Section 1\n\nYour content here...\n',
  outputFormat: 'custom',
  color: 'slate',
  reviewMode: 'edit',
  hasUnsavedChanges: false,
  originalContent: '',
};

// Simple markdown to HTML converter for platform template preview
const renderMarkdown = (content: string) => {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let inTable = false;
  let tableRows: string[][] = [];

  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const processInlineStyles = (text: string): React.ReactNode => {
    const inlineRegex = /\[([^\]]+)\]\(([^)]+)\)|\*\*(.+?)\*\*/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;
    let keyCounter = 0;

    while ((match = inlineRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      if (match[1] !== undefined && match[2] !== undefined) {
        const url = match[2];
        const isAnchorLink = url.startsWith('#');
        parts.push(
          <a
            key={`link-${keyCounter++}`}
            href={url}
            className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
            onClick={isAnchorLink ? (e) => {
              e.preventDefault();
              const targetId = url.slice(1);
              const targetElement = document.getElementById(targetId);
              if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            } : undefined}
          >
            {match[1]}
          </a>
        );
      } else if (match[3] !== undefined) {
        parts.push(<strong key={`bold-${keyCounter++}`}>{match[3]}</strong>);
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  const flushTable = () => {
    if (tableRows.length > 0) {
      elements.push(
        <div key={`table-${elements.length}`} className="overflow-x-auto my-4">
          <table className="min-w-full border-collapse border border-slate-300">
            <thead>
              <tr className="bg-slate-100">
                {tableRows[0]?.map((cell, i) => (
                  <th key={i} className="border border-slate-300 px-3 py-2 text-left text-sm font-semibold text-slate-700">
                    {processInlineStyles(cell.trim())}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.slice(2).map((row, rowIndex) => (
                <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="border border-slate-300 px-3 py-2 text-sm text-slate-600">
                      {processInlineStyles(cell.trim())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableRows = [];
    }
    inTable = false;
  };

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();

    if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
      if (!inTable) {
        inTable = true;
      }
      if (trimmedLine.match(/^\|[\s\-:|]+\|$/)) {
        tableRows.push([]);
        return;
      }
      const PIPE_PLACEHOLDER = '\u0000PIPE\u0000';
      const lineWithPlaceholder = trimmedLine.slice(1, -1).replace(/\\\|/g, PIPE_PLACEHOLDER);
      const cells = lineWithPlaceholder.split('|').map(cell =>
        cell.replace(new RegExp(PIPE_PLACEHOLDER, 'g'), '|')
      );
      tableRows.push(cells);
      return;
    } else if (inTable) {
      flushTable();
    }

    if (trimmedLine.startsWith('# ')) {
      const headingText = trimmedLine.slice(2);
      elements.push(
        <h1 key={index} id={generateSlug(headingText)} className="text-2xl font-bold text-slate-900 mt-6 mb-4 pb-2 border-b border-slate-200">
          {processInlineStyles(headingText)}
        </h1>
      );
    } else if (trimmedLine.startsWith('## ')) {
      const headingText = trimmedLine.slice(3);
      elements.push(
        <h2 key={index} id={generateSlug(headingText)} className="text-xl font-bold text-slate-800 mt-5 mb-3">
          {processInlineStyles(headingText)}
        </h2>
      );
    } else if (trimmedLine.startsWith('### ')) {
      const headingText = trimmedLine.slice(4);
      elements.push(
        <h3 key={index} id={generateSlug(headingText)} className="text-lg font-semibold text-slate-700 mt-4 mb-2">
          {processInlineStyles(headingText)}
        </h3>
      );
    } else if (trimmedLine.startsWith('#### ')) {
      const headingText = trimmedLine.slice(5);
      elements.push(
        <h4 key={index} id={generateSlug(headingText)} className="text-base font-semibold text-slate-600 mt-3 mb-2">
          {processInlineStyles(headingText)}
        </h4>
      );
    } else if (trimmedLine === '---' || trimmedLine === '***') {
      elements.push(<hr key={index} className="my-6 border-slate-200" />);
    } else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      elements.push(
        <li key={index} className="ml-6 text-slate-600 list-disc">
          {processInlineStyles(trimmedLine.slice(2))}
        </li>
      );
    } else if (/^\d+\.\s/.test(trimmedLine)) {
      elements.push(
        <li key={index} className="ml-6 text-slate-600 list-decimal">
          {processInlineStyles(trimmedLine.replace(/^\d+\.\s/, ''))}
        </li>
      );
    } else if (trimmedLine.startsWith('> ')) {
      elements.push(
        <blockquote key={index} className="border-l-4 border-blue-400 pl-4 py-2 my-2 bg-blue-50 text-slate-600 italic">
          {processInlineStyles(trimmedLine.slice(2))}
        </blockquote>
      );
    } else if (trimmedLine.length > 0) {
      elements.push(
        <p key={index} className="text-slate-600 my-2 leading-relaxed">
          {processInlineStyles(trimmedLine)}
        </p>
      );
    } else {
      elements.push(<div key={index} className="h-2" />);
    }
  });

  if (inTable) {
    flushTable();
  }

  return elements;
};

const Templates: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();

  // Default templates from DynamoDB
  const [defaultTemplates, setDefaultTemplates] = useState<DefaultTemplateMetadata[]>([]);
  const [defaultTemplatesLoading, setDefaultTemplatesLoading] = useState(true);
  const [defaultTemplatesError, setDefaultTemplatesError] = useState<string | null>(null);

  // Platform template modals
  const [previewModal, setPreviewModal] = useState<DefaultTemplateMetadata | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewContent, setPreviewContent] = useState<string>('');

  // Custom templates (user templates via useTemplates hook)
  const {
    templates: userTemplates,
    isLoading: userTemplatesLoading,
    error: userTemplatesError,
    refresh: refreshUserTemplates,
    getTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  } = useTemplates();

  // Project & assignment hooks
  const {
    projects,
    isLoading: projectsLoading,
    error: projectsError,
    createProject,
    updateProject,
    deleteProject: deleteProjectFn,
    refresh: refreshProjects,
  } = useProjectConfig();

  const { createAndAssign, unassignTemplate } = useAssignments();

  // "Add to My Templates" state (platform template → project picker)
  const [addingTemplate, setAddingTemplate] = useState<DefaultTemplateMetadata | null>(null);
  const [addingLoading, setAddingLoading] = useState(false);

  // Section collapse state (collapsed by default)
  const [projectsCollapsed, setProjectsCollapsed] = useState(false);
  const [unassignedCollapsed, setUnassignedCollapsed] = useState(true);

  // Assign custom template to project
  const [assigningCustomTemplate, setAssigningCustomTemplate] = useState<TemplateMetadata | null>(null);

  // ── Project management modal states ──
  const [projectModal, setProjectModal] = useState<{
    isOpen: boolean;
    editing: ProjectConfig | null;
  }>({ isOpen: false, editing: null });

  const [templatePickerProject, setTemplatePickerProject] = useState<ProjectConfig | null>(null);
  const [shareProject, setShareProject] = useState<ProjectConfig | null>(null);
  const [projectDeleteConfirm, setProjectDeleteConfirm] = useState<string | null>(null);

  // Compute unassigned templates (not in any project's templateIds)
  const unassignedTemplates = useMemo(() => {
    const assignedIds = new Set<string>();
    for (const project of projects) {
      for (const id of project.templateIds || []) {
        assignedIds.add(id);
      }
    }
    return userTemplates.filter(tmpl => !assignedIds.has(tmpl.id));
  }, [projects, userTemplates]);

  // Custom template editor state
  const [editor, setEditor] = useState<TemplateEditorState>(initialEditorState);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Clear action error after 5 seconds
  useEffect(() => {
    if (actionError) {
      const timer = setTimeout(() => setActionError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [actionError]);

  // Fetch default templates on mount
  useEffect(() => {
    const fetchDefaultTemplates = async () => {
      setDefaultTemplatesLoading(true);
      setDefaultTemplatesError(null);
      try {
        const templates = await templatesApi.listDefaultTemplates();
        setDefaultTemplates(templates);
      } catch (err) {
        setDefaultTemplatesError(err instanceof Error ? err.message : t('templates.failedToLoadTemplates'));
      } finally {
        setDefaultTemplatesLoading(false);
      }
    };
    fetchDefaultTemplates();
  }, []);

  // ── Platform template handlers ──

  const handlePreviewClick = async (template: DefaultTemplateMetadata) => {
    setPreviewModal(template);
    setPreviewLoading(true);
    setPreviewContent('');

    try {
      const fullTemplate = await templatesApi.getDefaultTemplate(template.templateId);
      setPreviewContent(fullTemplate.templateStructure || t('templates.noContentAvailable'));
    } catch {
      setPreviewContent(t('templates.failedToLoadPreview'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreviewModal = () => {
    setPreviewModal(null);
    setPreviewContent('');
  };

  const handleDownloadClick = async (template: DefaultTemplateMetadata) => {
    try {
      const fullTemplate = await templatesApi.getDefaultTemplate(template.templateId);
      const content = fullTemplate.templateStructure || '';
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.templateName.toLowerCase().replace(/\s+/g, '-')}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Silently fail — user can retry
    }
  };

  const handleDownloadCustomTemplate = async (template: TemplateMetadata) => {
    try {
      const { content } = await getTemplate(template.id);
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.name.toLowerCase().replace(/\s+/g, '-')}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Silently fail — user can retry
    }
  };

  // Check if a project already has a template with the given name
  const projectHasTemplateName = useCallback((projectId: string, templateName: string): boolean => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return false;
    const assignedIds = project.templateIds || [];
    return userTemplates.some(
      tmpl => assignedIds.includes(tmpl.id) && tmpl.name.toLowerCase() === templateName.toLowerCase()
    );
  }, [projects, userTemplates]);

  // "Add to My Templates" handler
  const handleAddToMyTemplates = async (template: DefaultTemplateMetadata, projectId: string) => {
    if (projectHasTemplateName(projectId, template.templateName)) {
      const projectName = projects.find(p => p.id === projectId)?.projectName || '';
      toast.error(t('templates.templateAlreadyInProject', { templateName: template.templateName, projectName }));
      return;
    }
    setAddingLoading(true);
    try {
      const fullTemplate = await templatesApi.getDefaultTemplate(template.templateId);
      const request: SaveTemplateRequest = {
        name: template.templateName,
        description: template.description,
        content: fullTemplate.templateStructure || '',
        outputFormat: template.outputFormat || 'custom',
        color: template.color || 'slate',
      };
      await createAndAssign(projectId, request);
      const projectName = projects.find(p => p.id === projectId)?.projectName || '';
      toast.success(t('templates.templateAddedToProject', { templateName: template.templateName, projectName }));
      refreshProjects();
      refreshUserTemplates();
      setAddingTemplate(null);
    } catch (err) {
      toast.error(errorMessages.addTemplate(err));
    } finally {
      setAddingLoading(false);
    }
  };

  // ── Custom template handlers ──

  const handleCreateTemplate = () => {
    const defaultContent = '# Template Title\n\n## Section 1\n\nYour content here...\n';
    setEditor({
      ...initialEditorState,
      isOpen: true,
      mode: 'create',
      content: defaultContent,
      originalContent: defaultContent,
      reviewMode: 'edit',
      hasUnsavedChanges: false,
    });
    setActionError(null);
  };

  const handleEditTemplate = async (template: TemplateMetadata) => {
    setActionError(null);
    try {
      const { content } = await getTemplate(template.id);
      const templateContent = content || '';
      setEditor({
        isOpen: true,
        mode: 'edit',
        templateId: template.id,
        name: template.name,
        description: template.description,
        content: templateContent,
        outputFormat: template.outputFormat || 'custom',
        color: template.color || 'slate',
        reviewMode: 'preview',
        hasUnsavedChanges: false,
        originalContent: templateContent,
      });
    } catch (err) {
      setActionError(errorMessages.loadTemplates(err));
    }
  };

  const handleContentEdit = useCallback((newContent: string) => {
    setEditor(prev => ({
      ...prev,
      content: newContent,
      hasUnsavedChanges: newContent !== prev.originalContent || prev.name !== prev.name,
    }));
  }, []);

  const handleCloseEditor = useCallback(() => {
    if (editor.hasUnsavedChanges) {
      if (!window.confirm(t('templates.unsavedChangesConfirm'))) {
        return;
      }
    }
    setEditor(initialEditorState);
  }, [editor.hasUnsavedChanges, t]);

  const handleSaveTemplate = async () => {
    if (!editor.name.trim()) {
      setActionError(t('templates.templateNameRequired'));
      return;
    }

    // Check for duplicate template name
    const trimmedName = editor.name.trim().toLowerCase();
    const duplicate = userTemplates.find(
      tmpl => tmpl.name.toLowerCase() === trimmedName &&
        (editor.mode !== 'edit' || tmpl.id !== editor.templateId)
    );
    if (duplicate) {
      setActionError(t('templates.templateNameExists', 'A template with this name already exists'));
      return;
    }

    setIsSaving(true);
    setActionError(null);

    try {
      const request: SaveTemplateRequest = {
        name: editor.name.trim(),
        description: editor.description.trim(),
        content: editor.content,
        outputFormat: editor.outputFormat,
        color: editor.color,
      };

      if (editor.mode === 'edit' && editor.templateId) {
        await updateTemplate(editor.templateId, request);
      } else {
        await createTemplate(request);
      }

      setEditor(initialEditorState);
    } catch (err) {
      setActionError(errorMessages.saveTemplate(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    setActionError(null);
    try {
      await deleteTemplate(templateId);
      setDeleteConfirm(null);
    } catch (err) {
      setActionError(errorMessages.deleteTemplate(err));
    }
  };

  // Assign an existing custom template to a project (creates a copy)
  const handleAssignCustomTemplate = useCallback(async (template: TemplateMetadata, projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    if (projectHasTemplateName(projectId, template.name)) {
      toast.error(t('templates.templateAlreadyInProject', { templateName: template.name, projectName: project.projectName }));
      setAssigningCustomTemplate(null);
      return;
    }
    try {
      const { content } = await getTemplate(template.id);
      const request: SaveTemplateRequest = {
        name: template.name,
        description: template.description,
        content,
        outputFormat: template.outputFormat,
        color: template.color,
      };
      await createAndAssign(projectId, request);
      refreshProjects();
      refreshUserTemplates();
      const projectName = project.projectName;
      toast.success(t('templates.templateAssignedToProject', { templateName: template.name, projectName }));
    } catch (err) {
      setActionError(errorMessages.saveTemplate(err));
    }
    setAssigningCustomTemplate(null);
  }, [projects, getTemplate, createAndAssign, refreshProjects, refreshUserTemplates, projectHasTemplateName, t]);

  // ── Project handlers (lifted from ProjectConfigPage) ──

  const handleCreateProject = useCallback(() => {
    setProjectModal({ isOpen: true, editing: null });
  }, []);

  const handleEditProject = useCallback((project: ProjectConfig) => {
    setProjectModal({ isOpen: true, editing: project });
  }, []);

  const handleSaveProject = useCallback(
    async (data: {
      projectName: string;
      type: ProjectConfig['type'];
      description?: string;
    }) => {
      setIsSaving(true);
      setActionError(null);
      try {
        if (projectModal.editing) {
          updateProject(projectModal.editing.id, {
            projectName: data.projectName,
            type: data.type,
            description: data.description,
          });
        } else {
          await createProject({
            projectName: data.projectName,
            type: data.type,
            description: data.description,
          });
        }
        setProjectModal({ isOpen: false, editing: null });
      } catch (err) {
        setActionError(t('projectConfig.failedToSaveProject'));
      } finally {
        setIsSaving(false);
      }
    },
    [projectModal.editing, createProject, updateProject, t]
  );

  const handleDeleteProject = useCallback((id: string) => {
    setProjectDeleteConfirm(id);
  }, []);

  const confirmDeleteProject = useCallback(async () => {
    if (projectDeleteConfirm) {
      setIsSaving(true);
      setActionError(null);
      try {
        const success = await deleteProjectFn(projectDeleteConfirm);
        if (success) {
          setProjectDeleteConfirm(null);
        } else {
          setActionError(t('projectConfig.failedToDeleteProject'));
        }
      } catch (err) {
        setActionError(t('projectConfig.failedToDeleteProject'));
      } finally {
        setIsSaving(false);
      }
    }
  }, [projectDeleteConfirm, deleteProjectFn, t]);

  const handleShareProject = useCallback((project: ProjectConfig) => {
    setShareProject(project);
  }, []);

  const handleTemplateAdded = useCallback((template: TemplateMetadata) => {
    if (!templatePickerProject) return;
    const currentIds = templatePickerProject.templateIds || [];
    if (currentIds.includes(template.id)) return;
    const updatedIds = [...currentIds, template.id];
    updateProject(templatePickerProject.id, { templateIds: updatedIds });
    setTemplatePickerProject({ ...templatePickerProject, templateIds: updatedIds });
    refreshUserTemplates();
  }, [templatePickerProject, updateProject, refreshUserTemplates]);

  const handleRemoveTemplate = useCallback(async (projectId: string, templateId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    const updatedIds = (project.templateIds || []).filter(id => id !== templateId);
    updateProject(projectId, { templateIds: updatedIds });
    unassignTemplate(projectId, templateId).catch(() => {});
    try {
      await deleteTemplate(templateId);
      refreshUserTemplates();
    } catch {
      // Copy deletion is best-effort
    }
  }, [projects, updateProject, unassignTemplate, deleteTemplate, refreshUserTemplates]);

  const formatDate = (dateString: string) => {
    return formatDateLocale(dateString);
  };

  if (authLoading) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
        </div>
      </PageLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <UnauthenticatedPage
        icon={<FileText className="w-8 h-8 text-blue-400" />}
        title={t('templates.unauthTitle', 'Document Templates')}
        description={t('templates.unauthDescription', 'Sign in to browse and use documentation templates for your projects.')}
      />
    );
  }

  return (
    <PageLayout variant="plain">
      <AppPageHeader
        icon={<FileText className="w-4 h-4" />}
        badge={t('templates.badge')}
        subtitle={t('templates.subtitle')}
      />

      {/* Content Area */}
      <section className="py-8 sm:py-10 bg-gradient-to-b from-slate-50 via-white to-indigo-50/30 min-h-[50vh]">
        <div className="container mx-auto px-6 max-w-6xl">

          {/* Error banner */}
          {(projectsError || userTemplatesError || actionError) && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{projectsError || userTemplatesError || actionError}</p>
            </div>
          )}

          {/* Loading state */}
          {(projectsLoading || userTemplatesLoading) && projects.length === 0 && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mr-3" />
              <span className="text-slate-600">{t('projectConfig.loadingProjects')}</span>
            </div>
          )}

          {/* ═══════════════ SECTION 1: My Projects (collapsible) ═══════════════ */}
          {!(projectsLoading && projects.length === 0) && (
            <section className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setProjectsCollapsed(prev => !prev)}
                  className="flex items-center gap-2 text-left group flex-1"
                >
                  <FolderKanban className="w-5 h-5 text-emerald-600" />
                  <h2 className="text-lg font-semibold text-slate-900">{t('projectConfig.myProjects')}</h2>
                  <span className="px-1.5 py-0.5 text-xs bg-slate-200 text-slate-600 rounded">
                    {projects.length}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${projectsCollapsed ? '-rotate-90' : ''}`} />
                </button>
                {!projectsCollapsed && (
                  <div className="flex items-center gap-2 sm:gap-3">
                    <button
                      onClick={handleCreateProject}
                      className="flex items-center gap-2 px-3 py-2 sm:px-4 bg-indigo-600 text-white rounded-lg
                               hover:bg-indigo-700 transition-colors text-sm sm:text-base"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="hidden sm:inline">{t('templates.addProject')}</span>
                      <span className="sm:hidden">{t('templates.create')}</span>
                    </button>
                  </div>
                )}
              </div>

              {!projectsCollapsed && (
                <>
                  {projects.length === 0 ? (
                    <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                      <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <h3 className="text-lg font-medium text-slate-900 mb-1">
                        {t('projectConfig.noProjectsYet')}
                      </h3>
                      <p className="text-slate-600 mb-4">
                        {t('projectConfig.noProjectsDescription')}
                      </p>
                      <button
                        onClick={handleCreateProject}
                        className="text-emerald-600 hover:text-emerald-700 font-medium"
                      >
                        {t('projectConfig.createFirstProject')}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {projects.map((project) => {
                        const assignedTemplates = userTemplates.filter((tmpl) =>
                          project.templateIds?.includes(tmpl.id)
                        );
                        return (
                          <ProjectCard
                            key={project.id}
                            project={project}
                            assignedTemplates={assignedTemplates}
                            onEdit={handleEditProject}
                            onDelete={handleDeleteProject}
                            onRemoveTemplate={handleRemoveTemplate}
                            onShare={handleShareProject}
                          />
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {/* ═══════════════ SECTION 2: Templates (collapsible) ═══════════════ */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setUnassignedCollapsed(prev => !prev)}
                className="flex items-center gap-2 text-left group flex-1"
              >
                <FileText className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-slate-900">
                  {t('templates.templates', 'Templates')}
                </h2>
                <span className="px-1.5 py-0.5 text-xs bg-slate-200 text-slate-600 rounded">
                  {unassignedTemplates.length + defaultTemplates.length}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${unassignedCollapsed ? '-rotate-90' : ''}`} />
              </button>
              {!unassignedCollapsed && (
                <button
                  onClick={handleCreateTemplate}
                  className="flex items-center gap-2 px-3 py-2 sm:px-4 bg-indigo-600 text-white rounded-lg
                           hover:bg-indigo-700 transition-colors text-sm sm:text-base"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('templates.newTemplate')}</span>
                  <span className="sm:hidden">{t('templates.create')}</span>
                </button>
              )}
            </div>

            {!unassignedCollapsed && (
              <>
                {/* ── Custom Templates subsection ── */}
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-3">
                    <Edit3 className="w-4 h-4 text-slate-400" />
                    <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">
                      {t('templates.customTemplates', 'Custom Templates')}
                    </h3>
                    <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-500 rounded">
                      {unassignedTemplates.length}
                    </span>
                  </div>

                  {unassignedTemplates.length === 0 ? (
                    <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
                      <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm text-slate-500">
                        {t('templates.noUnassignedCustom', 'All custom templates are assigned to projects.')}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {unassignedTemplates.map(template => {
                        const colors = getCustomColorClasses(template.color);
                        return (
                          <div
                            key={template.id}
                            className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <div className={`p-2 rounded-lg ${colors.bg}`}>
                                <FileText className={`w-5 h-5 ${colors.text}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-slate-900 truncate">{template.name}</h3>
                                <p className="text-sm text-slate-500">
                                  {t(OUTPUT_FORMATS.find(f => f.value === template.outputFormat)?.labelKey || 'templates.formatCustom')}
                                </p>
                              </div>
                            </div>
                            <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                              {template.description || t('templates.noDescription')}
                            </p>
                            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                              <span className="text-xs text-slate-400">
                                {t('templates.updated', { date: formatDate(template.lastUpdated) })}
                              </span>
                              <div className="flex items-center gap-1">
                                <CardAction color="indigo" iconOnly onClick={() => handleEditTemplate(template)} title={t('templates.edit')}>
                                  <Edit3 className="w-4 h-4" />
                                </CardAction>
                                <CardAction color="default" iconOnly onClick={() => handleDownloadCustomTemplate(template)} title={t('templates.download')}>
                                  <Download className="w-4 h-4" />
                                </CardAction>
                                <CardAction color="red" iconOnly onClick={() => setDeleteConfirm(template.id)} title={t('templates.delete')}>
                                  <Trash2 className="w-4 h-4" />
                                </CardAction>
                                <CardAction color="emerald" iconOnly onClick={() => setAssigningCustomTemplate(template)} title={t('templates.assignToProject', 'Assign to Project')}>
                                  <Plus className="w-4 h-4" />
                                </CardAction>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ── Platform Templates subsection ── */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-slate-400" />
                    <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">
                      {t('templates.platformTemplates')}
                    </h3>
                    <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-500 rounded">
                      {defaultTemplates.length}
                    </span>
                  </div>

                  {/* Loading state */}
                  {defaultTemplatesLoading && (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-600 mr-3" />
                      <span className="text-slate-600">{t('templates.loadingTemplates')}</span>
                    </div>
                  )}

                  {/* Error state */}
                  {defaultTemplatesError && !defaultTemplatesLoading && (
                    <div className="text-center py-8 bg-red-50 rounded-xl border border-red-200">
                      <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-red-400" />
                      <p className="text-red-600 mb-3">{defaultTemplatesError}</p>
                      <button
                        onClick={() => window.location.reload()}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                      >
                        <RefreshCw className="w-4 h-4" />
                        {t('templates.retry')}
                      </button>
                    </div>
                  )}

                  {/* Empty state */}
                  {!defaultTemplatesLoading && !defaultTemplatesError && defaultTemplates.length === 0 && (
                    <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
                      <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm text-slate-500">
                        {t('templates.noPlatformTemplatesDescription')}
                      </p>
                    </div>
                  )}

                  {/* Platform templates grid */}
                  {!defaultTemplatesLoading && !defaultTemplatesError && defaultTemplates.length > 0 && (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {defaultTemplates.map((template) => {
                        const colorClasses = getColorClasses(template.color || 'slate');
                        const IconComponent = ICON_MAP[template.icon || ''] || FileText;

                        return (
                          <div
                            key={template.templateId}
                            className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <div className={`p-2 rounded-lg ${colorClasses.bg}`}>
                                <IconComponent className={`w-5 h-5 ${colorClasses.icon}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-slate-900 truncate">{template.templateName}</h3>
                                <p className="text-sm text-slate-500">
                                  {template.outputFormat || t('templates.formatFull')}
                                </p>
                              </div>
                            </div>
                            <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                              {template.description || t('templates.noDescription')}
                            </p>
                            <div className="flex items-center justify-end pt-3 border-t border-slate-100">
                              <div className="flex items-center gap-1">
                                <CardAction color="blue" iconOnly onClick={() => handlePreviewClick(template)} title={t('templates.preview')}>
                                  <Eye className="w-4 h-4" />
                                </CardAction>
                                <CardAction color="default" iconOnly onClick={() => handleDownloadClick(template)} title={t('templates.download')}>
                                  <Download className="w-4 h-4" />
                                </CardAction>
                                <CardAction color="emerald" iconOnly onClick={() => setAddingTemplate(template)} title={t('templates.assignToProject', 'Assign to Project')}>
                                  <Plus className="w-4 h-4" />
                                </CardAction>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </section>

        </div>
      </section>

      {/* ═══════════════ MODALS ═══════════════ */}

      {/* Project Picker Modal for "Add to My Templates" */}
      {addingTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => !addingLoading && setAddingTemplate(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{t('templates.selectProject')}</h3>
                <p className="text-sm text-slate-500 mt-1">{t('templates.selectProjectDescription')}</p>
              </div>
              <CardAction color="default" iconOnly onClick={() => !addingLoading && setAddingTemplate(null)}>
                <X size={20} />
              </CardAction>
            </div>
            <div className="p-6 max-h-[50vh] overflow-y-auto">
              {projects.length === 0 ? (
                <div className="text-center py-8">
                  <FolderOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium text-slate-700">{t('templates.noProjects')}</p>
                  <p className="text-sm text-slate-500 mt-1">{t('templates.noProjectsDescription')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {projects.map(project => (
                    <button
                      key={project.id}
                      onClick={() => handleAddToMyTemplates(addingTemplate, project.id)}
                      disabled={addingLoading}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-left disabled:opacity-50"
                    >
                      <FolderOpen className="w-5 h-5 text-slate-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{project.projectName}</p>
                        {project.description && (
                          <p className="text-xs text-slate-500 truncate">{project.description}</p>
                        )}
                      </div>
                      {addingLoading && (
                        <Loader2 className="w-4 h-4 animate-spin text-indigo-600 shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Template → Project Assignment Modal */}
      {assigningCustomTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setAssigningCustomTemplate(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{t('templates.assignToProject', 'Assign to Project')}</h3>
                <p className="text-sm text-slate-500 mt-1">{assigningCustomTemplate.name}</p>
              </div>
              <CardAction color="default" iconOnly onClick={() => setAssigningCustomTemplate(null)}>
                <X size={20} />
              </CardAction>
            </div>
            <div className="p-6 max-h-[50vh] overflow-y-auto">
              {projects.length === 0 ? (
                <div className="text-center py-8">
                  <FolderOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium text-slate-700">{t('templates.noProjects')}</p>
                  <p className="text-sm text-slate-500 mt-1">{t('templates.createProjectFirst', 'Create a project first to assign templates.')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {projects.map(project => {
                    const isAlreadyAssigned = (project.templateIds || []).includes(assigningCustomTemplate.id);
                    return (
                      <button
                        key={project.id}
                        onClick={() => !isAlreadyAssigned && handleAssignCustomTemplate(assigningCustomTemplate, project.id)}
                        disabled={isAlreadyAssigned}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                          isAlreadyAssigned
                            ? 'border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed'
                            : 'border-slate-200 hover:border-indigo-300 hover:bg-indigo-50'
                        }`}
                      >
                        <FolderOpen className="w-5 h-5 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{project.projectName}</p>
                          {project.description && (
                            <p className="text-xs text-slate-500 truncate">{project.description}</p>
                          )}
                        </div>
                        {isAlreadyAssigned && (
                          <span className="text-xs text-slate-400 shrink-0">{t('templates.alreadyAssigned', 'Already assigned')}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Platform Template Preview Modal */}
      {previewModal && (() => {
        const PreviewIconComponent = ICON_MAP[previewModal.icon || ''] || FileText;
        const previewColorClasses = getColorClasses(previewModal.color || 'slate');

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              onClick={closePreviewModal}
            />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col animate-in fade-in zoom-in duration-200">
              <div className="flex items-center justify-between p-6 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${previewColorClasses.bg}`}>
                    <PreviewIconComponent size={20} className={previewColorClasses.icon} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">
                      {previewModal.templateName}
                    </h3>
                    <p className="text-sm text-slate-500">{t('templates.templatePreview')}</p>
                  </div>
                </div>
                <CardAction color="default" iconOnly onClick={closePreviewModal}>
                  <X size={20} />
                </CardAction>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {previewLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-slate-600">{t('templates.loadingPreview')}</span>
                  </div>
                ) : (
                  <div className="prose max-w-none">
                    {renderMarkdown(previewContent)}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Template Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('templates.deleteTemplate')}</h3>
            <p className="text-slate-600 mb-6">
              {t('templates.deleteTemplateWarning')}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {t('templates.cancel')}
              </button>
              <button
                onClick={() => handleDeleteTemplate(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('templates.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Delete Confirmation Modal */}
      {projectDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setProjectDeleteConfirm(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {t('projectConfig.deleteProject')}
            </h3>
            <p className="text-slate-600 mb-6">
              {t('projectConfig.deleteProjectWarning')}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setProjectDeleteConfirm(null)}
                className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {t('projectConfig.cancel')}
              </button>
              <button
                onClick={confirmDeleteProject}
                disabled={isSaving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? t('projectConfig.deleting') : t('projectConfig.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Modal (Create/Edit) */}
      <ProjectModal
        isOpen={projectModal.isOpen}
        onClose={() => setProjectModal({ isOpen: false, editing: null })}
        onSave={handleSaveProject}
        editingProject={projectModal.editing}
        isSaving={isSaving}
      />

      {/* Template Picker Modal (for project → add template) */}
      <TemplatePickerModal
        isOpen={templatePickerProject !== null}
        onClose={() => setTemplatePickerProject(null)}
        onTemplateAdded={handleTemplateAdded}
        projectId={templatePickerProject?.id}
      />

      {/* Share Project Modal */}
      {shareProject && (
        <ShareProjectModal
          isOpen={shareProject !== null}
          onClose={() => setShareProject(null)}
          project={shareProject}
        />
      )}

      {/* Custom Template Editor Modal */}
      {editor.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={handleCloseEditor}
          />
          {/* Panel */}
          <div className="relative bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-4xl sm:rounded-2xl shadow-2xl flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className={`p-2 rounded-lg ${getCustomColorClasses(editor.color).bgDark}`}>
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base sm:text-xl font-bold text-slate-900 truncate">
                      {editor.mode === 'create' ? t('templates.createTemplateTitle') : editor.name || t('templates.editTemplate')}
                    </h3>
                    {editor.hasUnsavedChanges && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                        {t('templates.unsaved')}
                      </span>
                    )}
                  </div>
                  {editor.mode === 'edit' && (
                    <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                      {t(OUTPUT_FORMATS.find(f => f.value === editor.outputFormat)?.labelKey || 'templates.formatCustom')}
                    </p>
                  )}
                </div>
              </div>

              {/* Preview/Edit Toggle */}
              <div className="flex items-center gap-2 mr-2">
                <div className="flex items-center bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => setEditor(prev => ({ ...prev, reviewMode: 'preview' }))}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                      editor.reviewMode === 'preview'
                        ? 'bg-white text-indigo-700 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">{t('templates.preview')}</span>
                  </button>
                  <button
                    onClick={() => setEditor(prev => ({ ...prev, reviewMode: 'edit' }))}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                      editor.reviewMode === 'edit'
                        ? 'bg-white text-indigo-700 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Edit3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">{t('templates.edit')}</span>
                  </button>
                </div>
              </div>

              <CardAction color="default" iconOnly onClick={handleCloseEditor}>
                <X className="w-5 h-5" />
              </CardAction>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-y-auto">
              {editor.reviewMode === 'preview' ? (
                <div className="px-4 sm:px-6 py-4">
                  <MarkdownPreview content={editor.content} />
                </div>
              ) : (
                <div className="p-4 sm:p-6 space-y-4">
                  {/* Name and Description row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t('templates.templateName')}
                      </label>
                      <input
                        type="text"
                        value={editor.name}
                        onChange={e => setEditor(prev => ({ ...prev, name: e.target.value, hasUnsavedChanges: true }))}
                        placeholder={t('templates.templateNamePlaceholder')}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t('templates.description')}
                      </label>
                      <input
                        type="text"
                        value={editor.description}
                        onChange={e => setEditor(prev => ({ ...prev, description: e.target.value, hasUnsavedChanges: true }))}
                        placeholder={t('templates.descriptionPlaceholder')}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>

                  {/* Output format and color */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t('templates.outputFormat')}
                      </label>
                      <select
                        value={editor.outputFormat}
                        onChange={e => setEditor(prev => ({ ...prev, outputFormat: e.target.value, hasUnsavedChanges: true }))}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {OUTPUT_FORMATS.map(format => (
                          <option key={format.value} value={format.value}>
                            {t(format.labelKey)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t('templates.color')}
                      </label>
                      <select
                        value={editor.color}
                        onChange={e => setEditor(prev => ({ ...prev, color: e.target.value, hasUnsavedChanges: true }))}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {COLOR_OPTIONS.map(color => (
                          <option key={color.value} value={color.value}>
                            {t(color.labelKey)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Content Editor */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t('templates.templateContent')}
                    </label>
                    <div className="border border-slate-300 rounded-lg overflow-hidden">
                      <WysiwygEditor
                        value={editor.content}
                        onChange={handleContentEdit}
                        placeholder={t('templates.templateContentPlaceholder')}
                        minHeight="min-h-[300px]"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {t('templates.templateContentHint')}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-200 bg-slate-50 sm:rounded-b-2xl">
              <button
                onClick={handleCloseEditor}
                disabled={isSaving}
                className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
              >
                {editor.hasUnsavedChanges ? t('templates.discard') : t('templates.cancel')}
              </button>
              <Button
                onClick={handleSaveTemplate}
                disabled={isSaving || !editor.name.trim()}
                variant="gradient-emerald"
                size="md"
                shape="default"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('templates.saving')}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {t('templates.saveTemplate')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default Templates;
